import React, { ReactElement, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { Link, Paths } from "../../../contexts/Routing";
import {
    FieldTypedValueInput, FormFieldReplacementInput, FormFieldType, SettingsTimeInput,
} from "../../../__generated__/api";
import EditableControl, { ControlType } from "../../editable-text/EditableText";
import { DEFAULT_VALUE_REPLACEMENTS, REPLACE_DEFAULT_GROUP, REPLACE_NOW_DATE } from "../../form-field/FieldTypeValueDisplay";
import SchedulePicker from "../../office-hours/OfficeHours";
import { ActionProps } from "./base";

const DATE_TIME_ID = "urn:fq:workflow-service-form-field:(urn:fq:workflow-service:system,date)";

const SCHEDULE_ID = "urn:fq:workflow-service-form-field:(urn:fq:workflow-service:system,schedule)";

const DEFAULT_REPLACEMENT = "{{default_properties/current_date_and_time}}";
export default function InHoursOfOperationSettings(props: ActionProps): ReactElement {
    const [shouldAllowOverwrite, setShouldAllowOverwrite] = useState<boolean>(
        () => props.values.some(row => row.value.fieldId === SCHEDULE_ID),
    );

    const [dateValue, setDateValue] = useState<FieldTypedValueInput | undefined>(
        () => props.values.find(row => row.value.fieldId === DATE_TIME_ID)?.value ?? {
            fieldId: DATE_TIME_ID,
            string: DEFAULT_REPLACEMENT,
            type: FormFieldType.DateTime,
        },
    );

    const [replacements, setReplacements] = useState<FormFieldReplacementInput[]>(
        () => props.values[0]?.replacement ?? [
            {
                fieldId: REPLACE_NOW_DATE,
                groupId: REPLACE_DEFAULT_GROUP,
                replacement: DEFAULT_REPLACEMENT,
            },
        ],
    );

    const [scheduleSettings, setScheduleSettings] = useState<SettingsTimeInput | undefined>(() => {
        const scheduleInput = props.values.find(row => row.value.fieldId === SCHEDULE_ID)?.value.string;
        return (scheduleInput ? JSON.parse(scheduleInput) : undefined);
    });

    useEffect(() => {
        props.onChange({
            replacements,
            values: [
                {
                    fieldId: DATE_TIME_ID,
                    type: FormFieldType.DateTime,
                    displayValue: dateValue?.displayValue,
                    date: dateValue?.date,
                    string: dateValue?.string,
                },
                scheduleSettings && shouldAllowOverwrite ? {
                    fieldId: SCHEDULE_ID,
                    type: FormFieldType.Text,
                    string: JSON.stringify(scheduleSettings),
                } : undefined,
            ].filterNull(),
        });
    }, [scheduleSettings, dateValue?.date, dateValue?.string, shouldAllowOverwrite]);

    return (
        <div className="">
            <Form.Label className="fw-bolder d-block">Date and Time</Form.Label>
            <small className="text-light d-gray-500">
                Specify the date and time which this task should look for when determining if its in
                the hours of operation or not.
                If not specified will default to the current date at the time this job is executed.
            </small>
            <EditableControl
                isReadOnly={false}
                isEditing
                isFormField
                isRequired={false}
                shouldFocus={false}
                placeholder="Date time"
                type={ControlType.DATE_TIME}
                replaceableInput={[...DEFAULT_VALUE_REPLACEMENTS, ...props.input]}
                value={dateValue?.date ?? dateValue?.string}
                replacements={replacements}
                onChange={(changed, replaceValues, displayText) => {
                    setDateValue({
                        type: FormFieldType.DateTime,
                        date: changed instanceof Date ? changed : undefined,
                        string: typeof changed === "string" ? changed : undefined,
                        displayValue: displayText,
                        fieldId: DATE_TIME_ID,
                    });
                    setReplacements(replaceValues ?? []);
                }}
            />

            <Form.Label className="fw-bolder d-block mt-4">Schedule</Form.Label>

            <div className="">
                <Form.Check
                    type="switch"
                    className=""
                    checked={shouldAllowOverwrite}
                    label="Customize schedule"
                    onChange={() => {
                        setShouldAllowOverwrite(value => !value);
                    }}
                />

                {shouldAllowOverwrite ? (
                    <div className="mt-4">
                        <SchedulePicker
                            shouldHaveBorders={false}
                            settings={scheduleSettings ?? {}}
                            shouldShowHeaders={false}
                            onChange={row => setScheduleSettings(row)}
                        />
                    </div>
                ) : (
                    <small className="text-light d-gray-500">
                        If the schedule is not specified here, this task will check the global
                        hours of operations schedule configured in the
                        {" "}
                        <Link
                            to={Paths.ADMINISTRATOR_SETTING}
                            params={{
                                setting: "time",
                            }}
                            target="_blank"
                        >
                            time settings.
                        </Link>
                    </small>
                )}

            </div>

        </div>

    );
}
